/**
 * Para onde voltar depois do login — e, na segunda metade, depois do CADASTRO.
 *
 * São dois destinos com dois ciclos de vida, e moram juntos para que a próxima
 * pessoa veja os dois antes de inventar um terceiro.
 *
 * ## O do login
 *
 * Quando quem interrompeu foi um 401.
 *
 * A guarda de rota (`RequireAuth`) passa o destino pelo `state` do router, que
 * é o caminho bom. Mas o 401 nasce em `lib/http.ts`, **fora** da árvore do
 * React Router: não há `navigate` nem `location` ali. Daí o `sessionStorage`,
 * que sobrevive ao reload duro que a interceptação faz e morre com a aba.
 *
 * `search` e `hash` fazem parte do destino e não são opcionais: o catálogo
 * carrega filtros e paginação na query string, e o link do WhatsApp pode
 * chegar com parâmetros. Devolver o cliente ao catálogo certo com os filtros
 * zerados é um bug, não um detalhe.
 */

const KEY = 'clearance-redirect-after-login';

export const LOGIN_PATH = '/login';

/** Caminho atual completo — o que o usuário veria na barra de endereços. */
export function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export function saveRedirectTarget(path: string = currentPath()): void {
  // Guardar `/login` faria o pós-login cair nele mesmo.
  if (path.startsWith(LOGIN_PATH)) return;
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    // Modo privativo/quota: perder o destino é aceitável, quebrar o login não.
  }
}

/** Lê e **consome** o destino — chamada duas vezes não repete o redirect. */
export function takeRedirectTarget(): string | null {
  try {
    const target = window.sessionStorage.getItem(KEY);
    if (target) window.sessionStorage.removeItem(KEY);
    return target;
  } catch {
    return null;
  }
}

/**
 * Para onde voltar depois do CADASTRO — outra chave, outro ciclo de vida.
 *
 * Não dá para reaproveitar a de cima, e por dois motivos independentes:
 *
 * 1. **`sessionStorage` morre com a aba**, e o funil de cadastro atravessa
 *    dias. É o mesmo raciocínio que põe o token do onboarding no
 *    `localStorage` (`use-onboarding.ts`) — as duas coisas têm de sobreviver
 *    juntas, ou a volta aponta para um cadastro que já não existe.
 * 2. **O destino chega enquanto a pessoa está em `/login`**, e
 *    `saveRedirectTarget()` sem argumento recusaria `/login` — corretamente,
 *    para o pós-login não cair em si mesmo. Aqui o caminho é o `from` da
 *    guarda, não a barra de endereços.
 *
 * A chave é consumida pelo fim do cadastro aprovado e pelo logout. Cadastro em
 * análise NÃO a limpa, por decisão: quem for aprovado dias depois e voltar pelo
 * mesmo navegador ainda chega onde o disparo prometeu.
 */
const ONBOARDING_KEY = 'clearance-onboarding-target';

export function saveOnboardingTarget(path: string): void {
  // Mesma guarda da de cima, pelo mesmo motivo: voltar do cadastro para o
  // login seria devolver a pessoa à tela que ela acabou de atravessar.
  if (path.startsWith(LOGIN_PATH)) return;
  try {
    window.localStorage.setItem(ONBOARDING_KEY, path);
  } catch {
    // Modo privativo/quota: perder o destino é aceitável, quebrar o cadastro não.
  }
}

/** Lê **sem consumir** — para montar um link que ainda não é a volta. */
export function peekOnboardingTarget(): string | null {
  try {
    return window.localStorage.getItem(ONBOARDING_KEY);
  } catch {
    return null;
  }
}

/** Lê e **consome**: a volta acontece uma vez. */
export function takeOnboardingTarget(): string | null {
  try {
    const target = window.localStorage.getItem(ONBOARDING_KEY);
    if (target) window.localStorage.removeItem(ONBOARDING_KEY);
    return target;
  } catch {
    return null;
  }
}

/** O `location` que o React Router entrega no `state` da navegação. */
export interface RedirectOrigin {
  pathname?: string;
  search?: string;
  hash?: string;
}

/**
 * Caminho completo → as três partes que o `state.from` do router carrega.
 *
 * O inverso de `resolveLoginRedirect`: é o que permite guardar um destino como
 * string e devolvê-lo ao login no formato que ele já sabe ler. Pelo `URL` e não
 * por `split`, porque a query pode conter `#` e o hash pode conter `?`.
 */
export function redirectOriginOf(path: string): RedirectOrigin {
  try {
    const url = new URL(path, window.location.origin);
    return { pathname: url.pathname, search: url.search, hash: url.hash };
  } catch {
    // Caminho impossível de parsear: devolve como pathname cru. O
    // `resolveLoginRedirect` concatena as três partes, então nada se perde.
    return { pathname: path };
  }
}

/**
 * Destino final do pós-login, com as duas origens em um lugar só.
 *
 * Precedência: o `state` do router vence, porque descreve a navegação que
 * acabou de ser interrompida; o `sessionStorage` é o resíduo de um 401
 * anterior e pode estar velho. O fallback é a home.
 *
 * Função pura em cima de `state` — o acesso ao `sessionStorage` fica no
 * `takeRedirectTarget`, que já trata browser sem storage.
 */
export function resolveLoginRedirect(
  state: unknown,
  storedTarget: string | null
): string {
  const from = (state as { from?: RedirectOrigin } | null)?.from;
  if (from?.pathname) {
    // `search` e `hash` não são enfeite: o catálogo carrega filtros e
    // paginação na query string. Voltar sem eles é voltar para outra tela.
    return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  }
  if (storedTarget) return storedTarget;
  return '/';
}
